<script setup>
import { computed, ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { menuRoutesEnum } from '@/router'
import router from '@/router'

import { useNotify } from '@/composables/notify'
import { useDialog } from '@/composables/dialog'

import { DxSplitter, DxItem } from 'devextreme-vue/splitter'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'
import { useColumns } from '@/composables/sale/electronic-invoice-grid'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import ElectronicInvoiceInfo from '@/components/sale/ElectronicInvoiceInfo.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import ElectronicInvoiceButtons from '@/components/sale/ElectronicInvoiceButtons.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'

import { usePortalStore } from '@/stores/portal.store'
import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

const gridId = 'electronic-invoice-grid'
const detailsGridRef = ref(null)
const { gridRef } = dataGrid.useGridRef()
const { applyFilter, setMultiSelectFilterEditorRef, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()
const { addWarning: addInvoiceWarning, displayWarnings: displayInvoicesWarnings } = dataGrid.useGenerateDocumentWarning()

const currentSaleDocumentPage = menuRoutesEnum.SALE_INVOICES

const i18n = getI18n()
const { notifySuccess, notifyError, notifyWarning } = useNotify()
const { genericDialog } = useDialog()

const portalStore = usePortalStore()
const saleStore = useSaleStore()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()

const selectedDocuments = ref([])
let hasTaxRegime = true

if (!registryStore.companyData?.tax_regime) {
  notifyWarning(i18n.t('common.missingTaxRegime'), {
    timeout: window.config.NOTIFICATION_DURATION,
    actions: [
      {
        label: i18n.t('common.complete_action'), size: 'sm', color: 'white', handler: async() => {
          const route = { path: menuRoutesEnum.COMPANY }
          await router.push(route)
        }
      }
    ]
  })
  hasTaxRegime = false
}

const disableSend = computed(() => {
  return !hasTaxRegime || (saleStore.electronicInvoiceCredit.value !== null && saleStore.electronicInvoiceCredit.value <= 0)
})

const columns = useColumns(currentSaleDocumentPage)

let transmitToolbarButton = null
let actionsToolbarButton = null

const refreshSummaryAsync = () => detailsGridRef.value?.refreshAsync()
const refreshInvoiceViewAsync = async() => {
  try {
    await gridRef.value?.instance?.refresh()
  } finally {
    await refreshSummaryAsync()
  }
}

const generateOrSendLabel = () => webSiteStore.user?.has_electronic_invoice_transmission ? `${i18n.t('common.submit')}` : `${i18n.t('common.generate')}`
const actionItems = [
  { action: portalStore.electronicInvoiceUserActionsMap.DOWNLOAD_XML.id, text: i18n.t('common.downloadXML'), icon: 'mdi mdi-download' },
  { action: portalStore.electronicInvoiceUserActionsMap.MANUAL_LOCK.id, text: i18n.t('common.manualLock'), icon: 'mdi mdi-lock' },
  { action: portalStore.electronicInvoiceUserActionsMap.CANCEL_XML.id, text: i18n.t('common.cancelXML'), icon: 'mdi mdi-file-remove-outline' },
  { action: portalStore.electronicInvoiceUserActionsMap.MANUAL_UPLOAD.id, text: i18n.t('common.manualUpload'), icon: 'mdi mdi-upload', visible: !webSiteStore.user.has_electronic_invoice_transmission },
  { action: portalStore.electronicInvoiceUserActionsMap.MANUAL_DELIVERY.id, text: i18n.t('common.manualDelivery'), icon: 'mdi mdi-handshake' },
  { action: portalStore.electronicInvoiceUserActionsMap.REJECTED_PUBLIC_ADMINISTRATION.id, text: i18n.t('common.rejectedByPublicAdministration'), icon: 'mdi mdi-close-circle' },
]
const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    {
      widget: 'dxDropDownButton',
      location: 'after',
      options: {
        icon: 'mdi mdi-cog',
        disabled: true,
        text: `${i18n.t('common.action', 2)}`,
        useSelectMode: false,
        displayExpr: 'text',
        dropDownOptions: { width: '16rem' },
        onItemClick: async(e) => {
          const ids = selectedDocuments.value.map(d => d.id)
          let statusUpdated = false
          if (e.itemData.action === portalStore.electronicInvoiceUserActionsMap.DOWNLOAD_XML.id) {
            let fileName = null
            if (ids.length > 1) {
              fileName = `${i18n.t('common.documentType.sale_invoice', 2)}.zip`
            }
            await saleStore.api.downloadElectronicInvoicesAsync({ ids: ids}, fileName, { reloadAsync: () => gridRef.value?.instance?.refresh() })
          } else {
            const data = { ids: ids, action: e.itemData.action }
            try {
              await saleStore.api.updateElectronicInvoiceStatuses(data, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
              statusUpdated = true
            } catch (err) {
              if (err?.response?.data?.warnings) {
                err?.response?.data?.warnings.forEach(i => {
                  i.messages.forEach(m => {
                    addInvoiceWarning(i.document, i18n.t(m))
                  })
                  displayInvoicesWarnings()
                })
              }
            }
          }
          gridRef.value?.instance.clearSelection()
          selectedDocuments.value = []
          if (statusUpdated) {
            await refreshInvoiceViewAsync()
          } else {
            await gridRef.value?.instance?.refresh()
          }
        },
        items: actionItems,
        onInitialized: (e) => { actionsToolbarButton = e.component }
      }
    },
    {
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'mdi mdi-send',
        disabled: true,
        text: generateOrSendLabel(),
        onClick: async() => {
          const ids = selectedDocuments.value.map(i => i.id)
          try {
            transmitToolbarButton.option('disabled', true)
            await saleStore.api.transmitElectronicInvoiceAsync({ ids: ids, send: true }, { skipErrorMessage: true })
            notifySuccess(i18n.t('common.xmlGenerated'))
          } catch (err) {
            const warnings = err?.response?.data?.warnings
            if (Array.isArray(warnings) && warnings.length > 0) {
              warnings.forEach(i => {
                i.messages?.forEach(m => { addInvoiceWarning(i.document, i18n.t(m)) })
              })
              displayInvoicesWarnings()
            } else {
              notifyError(i18n.t('common.transmissionError'), {
                timeout: 0,
                actions: [{ icon: 'mdi-close', color: 'white', round: true, handler: () => {} }],
              })
            }
          } finally {
            transmitToolbarButton.option('disabled', disableSend.value)
            gridRef.value?.instance.clearSelection()
            selectedDocuments.value = []
            await refreshInvoiceViewAsync()
          }
        },
        onInitialized: (e) => { transmitToolbarButton = e.component }
      }
    },
  )
  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const onSelectionChanged = async(e) => {
  selectedDocuments.value = e.selectedRowsData
  transmitToolbarButton.option('disabled',
    e.selectedRowsData.length === 0 || disableSend.value
  )

  if (e.selectedRowsData.length > 0) {
    const updatedItems = actionItems.map(item => {
      if (item.visible === undefined || item.visible) {
        let result = { ...item, visible: true }

        const actionConfig = Object.values(portalStore.electronicInvoiceUserActionsMap).find(a => a.id === item.action)

        if (actionConfig?.families) {
          const hasInvalidFamily = e.selectedRowsData.some(row => !actionConfig.families.includes(row.progress_status_family_code))
          result = { ...item, visible: !hasInvalidFamily }
        }

        if (item.action === portalStore.electronicInvoiceUserActionsMap.MANUAL_LOCK.id && e.selectedRowsData.every(row =>
          row.progress_status_code === portalStore.electronicInvoiceProgressStatusesEnum.MANUAL_LOCK || portalStore.electronicInvoiceUserActionsMap.MANUAL_LOCK.families.includes(row.progress_status_family_code)
        )) {
          result = { ...item, visible: true }
        }

        return result
      }
    }).filter(Boolean)

    actionsToolbarButton.option('items', updatedItems)
  }

  actionsToolbarButton.option('disabled', e.selectedRowsData.length === 0 || actionsToolbarButton.option('items').every(item => item !== undefined && !item?.visible))
}

const onClearFilter = () => {
  clearMultiSelectFilter(gridRef.value)
}

const onEditorPreparing = (e) => {
  if (e.parentType === 'filterRow' && e.dataField === 'document_type') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedDocumentTypes().filter(i => i.page === currentSaleDocumentPage),
      field: e.dataField,
      onInitialized: event => setMultiSelectFilterEditorRef(e.dataField, event.component),
    })
  }
  if (e.parentType === 'filterRow' && e.dataField === 'progress_status_family_code') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedElectronicInvoiceFamilies(),
      field: e.dataField,
      onInitialized: event => setMultiSelectFilterEditorRef(e.dataField, event.component),
    })
  }
  if (e.parentType === 'filterRow' && e.dataField === 'progress_status_code') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedElectronicInvoiceProgressStatuses(),
      field: e.dataField,
      onInitialized: event => setMultiSelectFilterEditorRef(e.dataField, event.component),
    })
  }
}

const getFilterDataSource = (field) => {
  const buildFilterOptions = (items) => [{
    items: [...items]
  }]

  switch (field) {
    case 'document_type':
      return buildFilterOptions(portalStore.getLocalizedDocumentTypes().filter(i => i.page === currentSaleDocumentPage))
    case 'progress_status_family_code':
      return buildFilterOptions(portalStore.getLocalizedElectronicInvoiceFamilies())
    case 'progress_status_code':
      return buildFilterOptions(portalStore.getLocalizedElectronicInvoiceProgressStatuses())
    default:
      return []
  }
}

const showInvoiceError = (error) => genericDialog(i18n.t('common.transmissionError'), error, { ok: true })

const dataSource = dataGrid.useODataSource(saleStore.api.getAbsoluteUrl() + saleStore.api.electronicInvoiceUrl)
const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataSource,
  columns: columns,
  selection: dataGrid.useDefaultGridSelection(),
  height: '100%',
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onEditorPreparing: onEditorPreparing,
  onClearFilter: onClearFilter,
})
</script>

<template>
  <div>
    <dx-splitter
      orientation="vertical"
      height="90vh"
    >
      <dx-item
        size="30%"
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <electronic-invoice-info
          ref="detailsGridRef"
          :grid-ref="gridRef"
          :apply-filter="applyFilter"
        />
      </dx-item>
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-data-grid
          ref="gridRef"
          v-bind="gridOptions"
        >
          <template #gridLayoutConfigTemplate>
            <grid-layout-config-template
              :grid-ext-ref="gridRef"
              :grid-ext-id="gridId"
            />
          </template>
          <template #documentTypeCellTemplate="{ data: templateOptions }">
            <chip-status :data="portalStore.getLocalizedDocumentTypes().find(x => x.id === templateOptions.data.document_type)" />
          </template>
          <template #electronicInvoiceFamilyCellTemplate="{ data: templateOptions }">
            <chip-status :data="portalStore.getLocalizedElectronicInvoiceFamilies().find(x => x.id === templateOptions.data.progress_status_family_code)" />
          </template>
          <template #electronicInvoiceErrorTemplate="{ data: cellInfo }">
            <q-btn
              v-if="cellInfo.data.error"
              icon="mdi-alert-circle-outline"
              color="negative"
              flat
              round
              size="sm"
              @click="showInvoiceError(cellInfo.data.error)"
            >
              <q-tooltip>{{ i18n.t('common.error') }}</q-tooltip>
            </q-btn>
          </template>
          <template #saleDocumentRef="{ data: cellInfo }">
            <reference-link
              v-if="!!cellInfo.data.sale_document_id"
              :data="{
                series: cellInfo.data.document_series,
                number: cellInfo.data.document_number_mnemonic
              }"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number']"
              :target="menuRoutesEnum.SALE_INVOICES"
            />
          </template>
          <template #multiSelectFilterRef="{ data }">
            <multi-select-filter
              :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
              :data-source="getFilterDataSource(data.component.option('field'))"
              :field="data.component.option('field')"
              :grid-ref="gridRef"
              :apply-filter="applyFilter"
              v-bind="{ data }"
            />
          </template>
          <template #electronicInvoiceButtonsTemplate="{ data: cellInfo }">
            <electronic-invoice-buttons
              :invoice="cellInfo.data"
              :grid-ref="gridRef"
              :refresh-summary-async="refreshSummaryAsync"
            />
          </template>
        </dx-data-grid>
      </dx-item>
    </dx-splitter>
  </div>
</template>

<style scoped lang="scss">
</style>
